import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar as CalendarIcon, MapPin, Clock, ChevronLeft, ChevronRight, Music, Users, Ticket, X, ExternalLink, DollarSign, Share2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API = "";

const EventCalendar = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState('calendar');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await fetch(`${API}/api/events?limit=100`);
      if (res.ok) {
        const data = await res.json();
        setEvents(data);
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);

  const getEventsForDate = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.date === dateStr);
  };

  const upcomingEvents = events
    .filter(e => new Date(e.date) >= new Date(new Date().toDateString()))
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const formatSelectedDate = (dateStr) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    return `${monthNames[m - 1]} ${d}`;
  };

  const formatEventDate = (dateStr) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const goToPrevMonth = () => setCurrentMonth(new Date(year, month - 1, 1));
  const goToNextMonth = () => setCurrentMonth(new Date(year, month + 1, 1));

  const handleDateClick = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(selectedDate === dateStr ? null : dateStr);
  };

  const openEventDetail = (event) => {
    setSelectedEvent(event);
    setDetailOpen(true);
  };

  const handleShare = async (event) => {
    const shareData = {
      title: event.title,
      text: `Check out ${event.title} at ${event.venue} on ${formatEventDate(event.date)}!`,
      url: window.location.href,
    };
    
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(`${event.title} - ${event.venue} - ${formatEventDate(event.date)}`);
        toast.success('Event details copied to clipboard!');
      }
    } catch (err) {
      console.error('Share failed:', err);
    }
  };

  const selectedDateEvents = selectedDate 
    ? events.filter(e => e.date === selectedDate)
    : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900/20 to-gray-900 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Event Card Component
  const EventListItem = ({ event, compact = false }) => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      whileHover={{ scale: 1.02 }}
      onClick={() => openEventDetail(event)}
      className={`p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-all cursor-pointer border border-transparent hover:border-purple-500/30 ${compact ? '' : 'p-4'}`}
    >
      <div className="flex gap-3">
        {event.image_url && (
          <img
            src={event.image_url}
            alt={event.title}
            className={`${compact ? 'w-16 h-16' : 'w-20 h-20'} rounded-lg object-cover`}
          />
        )}
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-white truncate">{event.title}</h4>
          <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
            <Clock size={12} />
            <span>{event.time}</span>
            {!compact && event.date && (
              <>
                <span className="mx-1">•</span>
                <span>{formatEventDate(event.date)}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <MapPin size={12} />
            <span className="truncate">{event.venue}</span>
          </div>
          {event.genre && (
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="secondary" className="text-[10px] bg-purple-500/20 text-purple-300">
                {event.genre}
              </Badge>
              {event.ticket_url && (
                <Badge variant="secondary" className="text-[10px] bg-green-500/20 text-green-300">
                  <Ticket size={10} className="mr-1" /> Tickets
                </Badge>
              )}
            </div>
          )}
        </div>
        <ChevronRight className="text-gray-500 self-center" size={20} />
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900/20 to-gray-900 pb-32 overflow-x-hidden">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 via-blue-600/10 to-purple-600/20" />
        <div className="relative px-2 sm:px-4 md:px-6 lg:px-8 py-6 sm:py-8">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-purple-500/20 rounded-xl">
                <CalendarIcon className="w-8 h-8 text-purple-400" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Event Calendar</h1>
                <p className="text-gray-400">Upcoming events in the Twin Cities</p>
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <Button
                onClick={() => setViewMode('calendar')}
                variant={viewMode === 'calendar' ? 'default' : 'outline'}
                size="sm"
                className={viewMode === 'calendar' ? 'bg-purple-600' : 'border-purple-500/50'}
              >
                <CalendarIcon size={16} className="mr-2" />
                Calendar
              </Button>
              <Button
                onClick={() => setViewMode('list')}
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                className={viewMode === 'list' ? 'bg-purple-600' : 'border-purple-500/50'}
              >
                <Users size={16} className="mr-2" />
                List View
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Event Registration CTA */}
      <div className="max-w-6xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 mt-4">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-purple-500/30 bg-gradient-to-r from-purple-900/30 via-gray-900/80 to-purple-900/30 p-4 flex flex-col sm:flex-row items-center gap-3 sm:justify-between"
          data-testid="event-register-cta"
        >
          <div className="text-center sm:text-left">
            <p className="font-bold text-sm text-white">Got a show coming up?</p>
            <p className="text-xs text-gray-400">List your event on Twin Tube and reach the local scene.</p>
          </div>
          <Link
            to="/register/event"
            className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs transition-colors"
            data-testid="event-register-link"
          >
            Register Your Event <ArrowRight size={14} />
          </Link>
        </motion.div>
      </div>

      <div className="px-2 sm:px-4 md:px-6 lg:px-8 py-6">
        <div className="max-w-6xl mx-auto">
          {viewMode === 'calendar' ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
              {/* Calendar */}
              <div className="lg:col-span-2 w-full overflow-hidden">
                <Card className="bg-white/5 border-white/10">
                  <CardContent className="p-1 sm:p-4 md:p-6">
                    <div className="flex items-center justify-between mb-4 sm:mb-6">
                      <Button onClick={goToPrevMonth} variant="ghost" size="icon" className="text-gray-400 hover:text-white h-8 w-8 sm:h-10 sm:w-10">
                        <ChevronLeft size={20} />
                      </Button>
                      <h2 className="text-lg sm:text-xl font-bold text-white">
                        {monthNames[month]} {year}
                      </h2>
                      <Button onClick={goToNextMonth} variant="ghost" size="icon" className="text-gray-400 hover:text-white h-8 w-8 sm:h-10 sm:w-10">
                        <ChevronRight size={20} />
                      </Button>
                    </div>

                    <div className="grid grid-cols-7 gap-0 sm:gap-1 mb-2">
                      {dayNames.map(day => (
                        <div key={day} className="text-center text-[10px] sm:text-sm text-gray-500 py-1 sm:py-2 min-w-0">
                          {day.substring(0, 1)}
                          <span className="hidden sm:inline">{day.substring(1)}</span>
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-7 gap-0 sm:gap-1">
                      {[...Array(startingDayOfWeek)].map((_, i) => (
                        <div key={`empty-${i}`} className="aspect-square min-w-0" />
                      ))}
                      
                      {[...Array(daysInMonth)].map((_, i) => {
                        const day = i + 1;
                        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                        const dayEvents = getEventsForDate(day);
                        const isToday = new Date().toDateString() === new Date(year, month, day).toDateString();
                        const isSelected = selectedDate === dateStr;
                        
                        return (
                          <motion.button
                            key={day}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleDateClick(day)}
                            className={`aspect-square min-w-0 p-0.5 sm:p-1 rounded-md sm:rounded-lg transition-colors relative flex flex-col items-center justify-center
                              ${isToday ? 'bg-purple-600/30 border border-purple-500' : ''}
                              ${isSelected ? 'bg-purple-600 text-white' : 'hover:bg-white/10'}
                              ${dayEvents.length > 0 ? 'font-bold' : ''}
                            `}
                          >
                            <span className={`text-[11px] sm:text-sm ${isSelected ? 'text-white' : isToday ? 'text-purple-400' : 'text-gray-300'}`}>
                              {day}
                            </span>
                            {dayEvents.length > 0 && (
                              <div className="absolute bottom-0.5 sm:bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                                {dayEvents.slice(0, 3).map((_, idx) => (
                                  <div
                                    key={idx}
                                    className={`w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full ${isSelected ? 'bg-white' : 'bg-purple-500'}`}
                                  />
                                ))}
                              </div>
                            )}
                          </motion.button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar Events */}
              <div>
                <Card className="bg-white/5 border-white/10">
                  <CardContent className="p-4">
                    <h3 className="text-lg font-bold text-white mb-4">
                      {selectedDate 
                        ? `Events on ${formatSelectedDate(selectedDate)}`
                        : 'Upcoming Events'
                      }
                    </h3>
                    
                    <div className="space-y-3 max-h-[500px] overflow-y-auto">
                      {(selectedDate ? selectedDateEvents : upcomingEvents.slice(0, 8)).map(event => (
                        <EventListItem key={event.id} event={event} compact />
                      ))}
                      
                      {(selectedDate ? selectedDateEvents : upcomingEvents).length === 0 && (
                        <p className="text-gray-500 text-center py-4">
                          {selectedDate ? 'No events on this date' : 'No upcoming events'}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : (
            /* List View - Enhanced with date grouping */
            <div className="space-y-6" data-testid="events-list-view">
              {(() => {
                // Group events by date
                const grouped = {};
                upcomingEvents.forEach(event => {
                  const d = event.date || 'Unknown';
                  if (!grouped[d]) grouped[d] = [];
                  grouped[d].push(event);
                });
                const sortedDates = Object.keys(grouped).sort();

                if (sortedDates.length === 0) {
                  return <p className="text-gray-500 text-center py-8">No upcoming events</p>;
                }

                return sortedDates.map(dateStr => {
                  const dayEvents = grouped[dateStr];
                  const dateObj = new Date(dateStr + 'T12:00:00');
                  const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
                  const monthDay = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  const isToday = new Date().toLocaleDateString() === dateObj.toLocaleDateString();

                  return (
                    <div key={dateStr}>
                      <div className="flex items-center gap-3 mb-3 sticky top-0 z-10 bg-gray-900/95 backdrop-blur py-2 -mx-2 px-2">
                        <div className={`w-12 h-12 rounded-lg flex flex-col items-center justify-center shrink-0 ${isToday ? 'bg-purple-600 text-white' : 'bg-white/5 border border-white/10'}`}>
                          <span className="text-[10px] font-semibold uppercase">{dateObj.toLocaleDateString('en-US', { month: 'short' })}</span>
                          <span className="text-lg font-bold leading-none">{dateObj.getDate()}</span>
                        </div>
                        <div>
                          <p className={`font-semibold text-sm ${isToday ? 'text-purple-400' : 'text-white'}`}>
                            {isToday ? 'Today' : dayName}
                          </p>
                          <p className="text-xs text-gray-500">{monthDay} &middot; {dayEvents.length} event{dayEvents.length > 1 ? 's' : ''}</p>
                        </div>
                      </div>
                      <div className="space-y-2 ml-0 sm:ml-[60px]">
                        {dayEvents.map(event => (
                          <EventListItem key={event.id} event={event} />
                        ))}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Event Detail Modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg p-0 overflow-hidden bg-gray-900 border-purple-500/30" hideClose>
          {selectedEvent && (
            <div>
              {/* Event Image */}
              {selectedEvent.image_url ? (
                <div className="relative h-48 sm:h-56">
                  <img
                    src={selectedEvent.image_url}
                    alt={selectedEvent.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent" />
                  <button
                    onClick={() => setDetailOpen(false)}
                    className="absolute top-3 right-3 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
                  >
                    <X size={20} />
                  </button>
                </div>
              ) : (
                <div className="relative h-32 bg-gradient-to-r from-purple-600/30 to-blue-600/30">
                  <button
                    onClick={() => setDetailOpen(false)}
                    className="absolute top-3 right-3 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
                  >
                    <X size={20} />
                  </button>
                </div>
              )}

              {/* Event Details */}
              <div className="p-5 sm:p-6">
                <h2 className="text-2xl font-bold text-white mb-2">{selectedEvent.title}</h2>
                
                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {selectedEvent.genre && (
                    <Badge className="bg-purple-500/20 text-purple-300">
                      <Music size={12} className="mr-1" /> {selectedEvent.genre}
                    </Badge>
                  )}
                  {selectedEvent.vibe && (
                    <Badge className="bg-blue-500/20 text-blue-300">
                      {selectedEvent.vibe}
                    </Badge>
                  )}
                  {selectedEvent.price_tier && (
                    <Badge className="bg-green-500/20 text-green-300">
                      <DollarSign size={12} className="mr-1" /> {selectedEvent.price_tier}
                    </Badge>
                  )}
                </div>

                {/* Date & Time */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3 text-gray-300">
                    <div className="p-2 bg-purple-500/20 rounded-lg">
                      <CalendarIcon size={18} className="text-purple-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">{formatEventDate(selectedEvent.date)}</p>
                      <p className="text-sm text-gray-400">{selectedEvent.time}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 text-gray-300">
                    <div className="p-2 bg-purple-500/20 rounded-lg">
                      <MapPin size={18} className="text-purple-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">{selectedEvent.venue}</p>
                      {selectedEvent.artist_name && (
                        <p className="text-sm text-gray-400">Featuring: {selectedEvent.artist_name}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Description */}
                {selectedEvent.description && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-400 mb-2">About This Event</h3>
                    <p className="text-gray-300 text-sm leading-relaxed">{selectedEvent.description}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="space-y-3">
                  {selectedEvent.ticket_url && (
                    <a
                      href={selectedEvent.ticket_url.startsWith('http') ? selectedEvent.ticket_url : `https://${selectedEvent.ticket_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold rounded-lg transition-all"
                    >
                      <Ticket size={20} />
                      Buy Tickets
                      <ExternalLink size={16} />
                    </a>
                  )}
                  
                  <div className="flex gap-3">
                    <Button
                      onClick={() => handleShare(selectedEvent)}
                      variant="outline"
                      className="flex-1 border-purple-500/50 text-purple-300 hover:bg-purple-500/20"
                    >
                      <Share2 size={16} className="mr-2" />
                      Share Event
                    </Button>
                    <Button
                      onClick={() => setDetailOpen(false)}
                      variant="outline"
                      className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventCalendar;
